import { useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  dictationSessionAtom,
  dictationStageAtom,
  dictationLiveTranscriptAtom,
  dictationHistoryAtom,
  dictationDeviceAtom,
  dictationErrorAtom,
  type DictationStage,
  type DictationSession,
  type DictationHistoryEntry,
} from "../../atoms/dictationSessionAtoms";
import { micTestOpenAtom } from "../../atoms/dictationModalAtoms";
import { useDictationActions } from "./DictationProvider";
import { DICTATION_WS_URL } from "@/hooks/useRealtimeTranscription";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/design-system/components/react";
import { logger } from "../../utils/logger";

function StageChip({ stage }: { stage: DictationStage }) {
  const color =
    stage === "listening"
      ? "bg-signal"
      : stage === "finalizing" || stage === "delivering"
        ? "bg-surface-2"
        : stage === "ready"
          ? "bg-signal"
          : stage === "connecting"
            ? "bg-surface-2"
            : "bg-bg-2";
  return (
    <span
      data-testid="mic-test-stage"
      className={`inline-block px-2 py-1 rounded text-xs font-mono text-white ${color}`}
    >
      {stage}
    </span>
  );
}

function VuMeter({ level }: { level: number | null }) {
  const pct = Math.min(100, Math.max(0, (level ?? 0) * 100));
  return (
    <div
      data-testid="mic-test-vu"
      className="w-full h-3 bg-surface rounded overflow-hidden"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={level ?? 0}
    >
      <div className="h-full bg-signal" style={{ width: `${pct}%` }} />
    </div>
  );
}

function HistoryRow({ entry }: { entry: DictationHistoryEntry }) {
  const [copyStatus, setCopyStatus] = useState("Copy");
  const copyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(entry.postprocessed || entry.text);
      setCopyStatus("Copied");
    } catch (err) {
      logger.warn("dictation_transcript_copy_failed", err);
      setCopyStatus("Copy failed");
    }
  };
  const kind = entry.dispatch.kind;
  let detail = "";
  if (kind === "insert") {
    detail = entry.dispatch.targetChipId
      ? `→ chip ${entry.dispatch.targetChipId}`
      : `→ pos ${entry.dispatch.pos ?? "?"}`;
  } else if (kind === "dropped") {
    detail = `dropped (${entry.dispatch.reason})`;
  } else {
    detail = `command ${entry.dispatch.command}`;
  }
  return (
    <div
      data-testid={`mic-test-history-row-${entry.ts}`}
      className="flex justify-between text-xs font-mono py-1 border-b border-border-strong"
    >
      <span className="truncate flex-1">{entry.postprocessed || entry.text}</span>
      <span className="text-fg-muted ml-2 whitespace-nowrap">{detail}</span>
      <button
        type="button"
        onClick={copyTranscript}
        className="ml-2"
        aria-label="Copy transcript"
        disabled={!entry.text}
      >
        {copyStatus}
      </button>
    </div>
  );
}

function CopyDiagnostic({ session }: { session: DictationSession }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    // PII scrub: device labels are often the user's name ("John's AirPods
    // Pro", "Andrea's MacBook Pro Microphone"). Strip to a short deviceId
    // prefix so the diagnostic is safe to paste into a ticket or Slack.
    // userAgent stays — it's standard ops data and useful for browser-
    // specific bug triage.
    const selectedDeviceId = session.device.selectedId;
    const deviceIdPrefix = selectedDeviceId ? selectedDeviceId.slice(0, 6) : null;
    const payload = {
      provider: "openai-realtime",
      ws_url: DICTATION_WS_URL,
      stage: session.stage,
      mode: session.mode,
      device: {
        idPrefix: deviceIdPrefix,
        quality: session.device.quality,
        applied: session.device.appliedConstraints,
        level: session.device.inputLevel,
      },
      lastInterim: session.lastInterim,
      history: session.history,
      error: session.error,
      browser: navigator.userAgent,
    };
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((err) => {
        logger.warn("mic_test_clipboard_write_failed err=%o", err);
      });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="w-full py-2 rounded bg-surface hover:bg-surface-2 text-xs"
    >
      {copied ? "Copied!" : "Copy diagnostic JSON"}
    </button>
  );
}

/**
 * Mic settings + STT pipeline diagnostic surface. Mounted as a Radix
 * Dialog from the control bar's settings gear → "Mic settings". Houses the mic
 * device selector AND a Hold-to-test surface that verifies the full
 * pipeline (audio → transcriber → finalized transcript). Read-only mirror
 * of the live pipeline state otherwise.
 */
export function MicTestPanel() {
  const [open, setOpen] = useAtom(micTestOpenAtom);
  const session = useAtomValue(dictationSessionAtom);
  const setSession = useSetAtom(dictationSessionAtom);
  const stage = useAtomValue(dictationStageAtom);
  const liveTranscript = useAtomValue(dictationLiveTranscriptAtom);
  const history = useAtomValue(dictationHistoryAtom);
  const device = useAtomValue(dictationDeviceAtom);
  const error = useAtomValue(dictationErrorAtom);
  const actions = useDictationActions();

  const canTest = stage === "ready";
  const newestFirst = [...history].reverse();

  // Dialog dismissal (Esc / X / outside-click) while the Hold-to-test
  // button is held unmounts the button before any pointer event can fire,
  // so onPointerUp / onPointerCancel never run and the pipeline stays
  // stuck in `listening`. Tear it down from onOpenChange instead.
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && stage === "listening") {
          actions.stopDictation();
        }
        setOpen(next);
      }}
    >
      <DialogContent className="max-w-xl bg-surface-2 border-border-strong text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            Mic settings <StageChip stage={stage} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {error && (
            <div className="bg-warn-tint border border-warn rounded p-2 flex items-center justify-between gap-2">
              <span className="flex-1">{error}</span>
              {stage !== "ready" && (
                <button
                  type="button"
                  onClick={() => actions.connect()}
                  className="px-2 py-1 rounded bg-signal hover:bg-signal/90 text-xs whitespace-nowrap"
                >
                  Reconnect
                </button>
              )}
            </div>
          )}

          <section>
            <div className="text-xs text-fg-muted mb-1">Input level</div>
            <VuMeter level={device.inputLevel} />
          </section>

          <section>
            <div className="text-xs text-fg-muted mb-1">Device</div>
            <div className="font-mono text-xs">
              {device.available.find((d) => d.deviceId === device.selectedId)?.label ?? "—"}
              <span className="ml-2 text-fg-muted">
                quality: {device.quality ?? "—"} | NS:{" "}
                {String(device.appliedConstraints?.noiseSuppression ?? "—")} | rate:{" "}
                {device.appliedConstraints?.sampleRate ?? "—"}
              </span>
            </div>
            {device.available.length > 1 && (
              <select
                className="mt-1 text-xs w-full bg-bg-2 border border-border-strong rounded p-1"
                value={device.selectedId ?? ""}
                onChange={(e) => actions.selectMicDevice(e.target.value)}
                aria-label="Microphone"
              >
                {device.available.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            )}
          </section>

          <section>
            <div className="text-xs text-fg-muted mb-1">Transcript</div>
            <div
              data-testid="mic-test-transcript"
              className="font-mono text-xs bg-bg-2 rounded p-2 min-h-10 leading-relaxed"
            >
              {liveTranscript.finalized && (
                <span data-testid="mic-test-finalized">{liveTranscript.finalized}</span>
              )}
              {liveTranscript.finalized && liveTranscript.interim && " "}
              {liveTranscript.interim && (
                <em data-testid="mic-test-interim" className="text-fg-muted">
                  {liveTranscript.interim}
                </em>
              )}
              {!liveTranscript.finalized && !liveTranscript.interim && (
                <span className="text-fg-muted">—</span>
              )}
            </div>
          </section>

          <section>
            <div className="text-xs text-fg-muted mb-1">Dispatch history</div>
            <div className="bg-bg-2 rounded p-2">
              {newestFirst.length === 0 ? (
                <span className="text-fg-muted text-xs">—</span>
              ) : (
                newestFirst.map((e) => <HistoryRow key={e.ts} entry={e} />)
              )}
            </div>
          </section>

          <button
            type="button"
            disabled={!canTest}
            // Pointer-capture so the release fires even when the user
            // drags off the button mid-hold. Without it, dismissing the
            // modal via Esc/X mid-hold skips onMouseUp and the pipeline
            // stays in `listening` until unmount.
            onPointerDown={(e) => {
              if (!canTest) return;
              e.currentTarget.setPointerCapture(e.pointerId);
              // Pin mode to "smart" — the test panel is mode-agnostic, but
              // dictationSessionAtom.mode is sticky across the chord
              // handlers in PrototypeReport. Without this, a prior Ctrl+`
              // leaves mode at "voice_adjust" and a Hold-to-test final
              // routes into the refine path.
              setSession((s) => ({ ...s, mode: "smart" }));
              actions.startDictation();
            }}
            onPointerUp={(e) => {
              if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
              }
              actions.stopDictation();
            }}
            onPointerCancel={(e) => {
              if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
              }
              actions.stopDictation();
            }}
            className="w-full py-2 rounded bg-signal hover:bg-signal/90 disabled:opacity-50"
          >
            Hold to test
          </button>

          <CopyDiagnostic session={session} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
